import type {CreateExtArgs, CreateExtensionArgument, DmApis, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {ClientSpecMap, CompRef} from '@wix/document-services-types'
import {pointerType, type RMApi} from '../rendererModel'
import {ReportableError} from '@wix/document-manager-utils'
import getPageJson from '../fixers/getPageJson'
import {MASTER_PAGE_ID} from '../../constants/constants'
import {fetchWithCompress} from '../../utils/fetchUtils'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {AppCSMData, EnrichedCSM} from '@wix/document-services-types/dist/src/miniSite'
import {findAppByDefinitionId} from '../tpa/installedTpaAppsUtils'

const FAKE_HOME_PAGE_JSON_FILE_NAME = 'fakePageJsonFileName.json'
export interface MiniSiteExtensionAPI extends ExtensionAPI {
    miniSite: {
        loadCSM(msId: string): Promise<void>
        getLoadedCSM(msId: string): ClientSpecMap | void
        updateCSMForMSID(msId: string, csm: ClientSpecMap): void
        createPreviewElementsHTML(pagePointer: CompRef, previewElementOptions?: EnrichedCSM): any
        loadCSMForCurrentMSID(): void
        enrichClientSpecMap(clientSpecMap: Record<string, any>, enrichedCSMW: AppCSMData[]): void
    }
}

const getPublicCSMEndpoint = (msId: string) =>
    `/_api/public-csm-server/v1/client-spec-map/public/${msId}?doNotMutate=true&https=true`

const createExtension = ({environmentContext}: CreateExtensionArgument): Extension => {
    let currentMSID: string
    const loadedCSM: Map<string, ClientSpecMap> = new Map()
    const {fetchFn} = environmentContext

    const createExtensionAPI = ({extensionAPI, dal, pointers, coreConfig}: CreateExtArgs): MiniSiteExtensionAPI => {
        const {rendererModel, siteAPI} = extensionAPI as RMApi

        const fetchCSMForMSID: (msId: string) => Promise<ClientSpecMap> = async (msId: string) => {
            let response: Response
            const interaction = 'MiniSitesFetchCsmFromPublic'
            try {
                coreConfig.logger.interactionStarted(interaction)
                response = await fetchFn(getPublicCSMEndpoint(msId))
                const result = await response!.json()
                coreConfig.logger.interactionEnded(interaction)
                return result
            } catch (e) {
                const err = new ReportableError({
                    message: 'Cannot fetch client spec map from public',
                    errorType: 'FETCH_PUBLIC_CSM_FOR_MINISITES_FAILURE',
                    extras: {
                        //@ts-ignore
                        status: response?.status
                    }
                })
                coreConfig.logger.captureError(err)
                throw err
            }
        }

        const loadCSM = async (msId: string) => {
            if (currentMSID === msId) {
                return
            }
            const currentCSM = dal.get(pointers.general.getClientSpecMap())
            loadedCSM.set(currentMSID, currentCSM)
            if (!loadedCSM.has(msId)) {
                const csm = await fetchCSMForMSID(msId)
                loadedCSM.set(msId, csm)
            }
        }

        const getLoadedCSM = (msId: string) => {
            if (loadedCSM.has(msId)) {
                return loadedCSM.get(msId)
            }
            console.error(`Tried to get loaded CSM for msid ${msId} from miniSite but the CSM has not been loaded yet`)
        }

        const updateCSMForMSID = (msId: string, csm: ClientSpecMap) => {
            if (!currentMSID) {
                currentMSID = rendererModel.getMetaSiteId()
            }
            if (currentMSID === msId) {
                dal.set(pointers.general.getClientSpecMap(), csm)
            }
            loadedCSM.set(msId, csm)
        }

        const enrichClientSpecMap = (clientSpecMap: Record<string, any>, enrichedCSMW: AppCSMData[]) => {
            for (const {msid, appDefinitionId} of enrichedCSMW) {
                const msidCSMApps = loadedCSM.get(msid)
                const appCSM = findAppByDefinitionId(msidCSMApps, appDefinitionId)

                if (!appCSM) {
                    throw new ReportableError({
                        errorType: 'appCSMUndefined',
                        message: 'App CSM does not exist locally',
                        extras: {msid, appDefinitionId}
                    })
                }

                const newKey = Object.keys(clientSpecMap).length
                    ? Math.max(...Object.keys(clientSpecMap).map(Number)) + 1
                    : 1
                clientSpecMap[newKey] = appCSM
            }
        }

        const enrichRenderedModel = (
            clonedRenderedModel: Record<string, any>,
            page: any,
            enrichedCSMW?: AppCSMData[]
        ) => {
            const {title, pageUriSEO, id: pageId} = page

            clonedRenderedModel.runningExperiments = {
                ...clonedRenderedModel.runningExperiments,
                bypassSsrInternalCache: 'true'
            }

            clonedRenderedModel.pageList = {
                ...clonedRenderedModel.pageList,
                pages: [{pageId, title, pageUriSEO, pageJsonFileName: FAKE_HOME_PAGE_JSON_FILE_NAME}],
                mainPageId: pageId
            }

            clonedRenderedModel.passwordProtectedPages = clonedRenderedModel.passwordProtectedPages.pages

            if (enrichedCSMW) {
                enrichClientSpecMap(clonedRenderedModel.clientSpecMap, enrichedCSMW)
            }
        }

        const createPreviewElementsHTML = async (pagePointer: CompRef, previewElementOptions?: EnrichedCSM) => {
            const {id: pageId} = pagePointer

            const fetchPageJson = (id: string) => getPageJson(id, {dal, extensionAPI})
            const [pageJson, masterPageJson] = [fetchPageJson(pageId), fetchPageJson(MASTER_PAGE_ID)]

            const renderedModel = deepClone(
                dal.query(pointerType, dal.queryFilterGetters.getRendererModelFilter(pointerType))
            )

            const {masterPageJsonFileName} = renderedModel.pageList
            const {mainPageId} = masterPageJson.data.document_data.masterPage
            const page = masterPageJson.data.document_data[mainPageId]

            enrichRenderedModel(renderedModel, page, previewElementOptions?.enrichedCSM)

            const body = {
                masterPage: {
                    id: MASTER_PAGE_ID,
                    jsonFileName: masterPageJsonFileName,
                    content: JSON.stringify(masterPageJson)
                },
                pages: [
                    {
                        id: mainPageId,
                        jsonFileName: FAKE_HOME_PAGE_JSON_FILE_NAME,
                        content: JSON.stringify(pageJson).replace(pageId, mainPageId)
                    }
                ],
                renderModel: renderedModel
            }

            return fetchWithCompress(
                '/_api/ephemeral-site-renderer/v1/render-site',
                siteAPI.getInstance(),
                fetchFn,
                'POST',
                body
            )
        }

        const loadCSMForCurrentMSID = async () => {
            await rendererModel.refreshClientSpecMap()
            const csm = dal.get(pointers.general.getClientSpecMap())
            loadedCSM.set(rendererModel.getMetaSiteId(), csm)
        }

        return {
            miniSite: {
                loadCSM,
                getLoadedCSM,
                updateCSMForMSID,
                createPreviewElementsHTML,
                loadCSMForCurrentMSID,
                enrichClientSpecMap
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {miniSite} = extensionAPI as MiniSiteExtensionAPI
        return {
            miniSite: {
                loadCSM: miniSite.loadCSM,
                createPreviewElementsHTML: miniSite.createPreviewElementsHTML,
                loadCSMForCurrentMSID: miniSite.loadCSMForCurrentMSID
            }
        }
    }

    return {
        name: 'miniSite',
        dependencies: new Set(['rendererModel']),
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
